import dayjs, { Dayjs } from 'dayjs';
import capitalize from 'lodash/capitalize';
import omit from 'lodash/omit';
import { ChangeEvent, Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useRegisterShareholderList } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import { IssuerDocumentFilters } from '@/modules/issuer-dashboard/pages/IssuerDocuments.tsx';
import { FilterItem, SelectOption } from 'ui';
import { DocumentOperationType, EntityStatus } from 'api';

const operationTypeOptions: SelectOption<DocumentOperationType>[] = Object.values(
  DocumentOperationType,
).map(value => ({
  label: capitalize(value),
  value,
}));

interface IssuerDocumentsFiltersProps {
  filters: IssuerDocumentFilters;
  setFilters: Dispatch<SetStateAction<IssuerDocumentFilters>>;
}

const IssuerDocumentsFilters = ({ filters, setFilters }: IssuerDocumentsFiltersProps) => {
  const [params] = useSearchParams();
  const registerId = +(params.get(REGISTER_ID_SEARCH_PARAM_KEY) || '0');

  const handleChangeHolder = useCallback(
    (shareholder_id: number | null) => {
      setFilters(filters =>
        shareholder_id ? { ...filters, shareholder_id } : omit(filters, 'shareholder_id'),
      );
    },
    [setFilters],
  );

  const handleChangeOperationType = useCallback(
    (operation_type: IssuerDocumentFilters['operation_type'] | null) => {
      setFilters(filters =>
        operation_type ? { ...filters, operation_type } : omit(filters, 'operation_type'),
      );
    },
    [setFilters],
  );

  const handleChangeOperationId = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const operation_id = +event.target.value;
      if (isNaN(operation_id)) return;

      setFilters(filters =>
        operation_id ? { ...filters, operation_id } : omit(filters, 'operation_id'),
      );
    },
    [setFilters],
  );

  const { data: registerShareholderList } = useRegisterShareholderList(
    { register_id: registerId, entity_status: Object.values(EntityStatus).join(',') },
    { disabled: !registerId },
  );

  const registerShareholderListOptions = useMemo<SelectOption<number>[]>(
    () =>
      registerShareholderList?.shareholders?.map(({ id, entity: { entity_name } }) => ({
        label: entity_name,
        value: id,
      })) || [],
    [registerShareholderList],
  );

  const handleChangeDates = useCallback(
    (dates: [Dayjs, Dayjs] | null) => {
      setFilters(filters =>
        dates
          ? {
              ...filters,
              date_from: dates[0].toISOString(),
              date_to: dates[1].endOf('d').toISOString(),
            }
          : omit(filters, ['date_from', 'date_to']),
      );
    },
    [setFilters],
  );
  return (
    <div className="flex flex-wrap gap-2 border-t pt-2 px-3 sm:px-6 [&>div>div>div]:min-w-[100px]">
      <FilterItem
        label=""
        className="[&>div]:min-w-[215px]" //325px
        type="select"
        value={filters.shareholder_id}
        options={registerShareholderListOptions}
        onChange={handleChangeHolder}
        placeholder="Holder"
      />
      <FilterItem
        label=""
        type="select"
        value={filters.operation_type}
        options={operationTypeOptions}
        onChange={handleChangeOperationType}
        placeholder="Operation type"
      />
      <FilterItem type="custom">
        <input
          type="text"
          value={filters.operation_id || ''}
          placeholder="Operation ID"
          className="w-[130px] outline-none text-sm"
          onChange={handleChangeOperationId}
        />
      </FilterItem>
      <FilterItem
        type="date"
        placeholder="Date"
        showIcon={false}
        onChange={handleChangeDates}
        value={
          filters.date_from && filters.date_to
            ? [dayjs(filters.date_from), dayjs(filters.date_to)]
            : undefined
        }
      />
    </div>
  );
};

export default IssuerDocumentsFilters;
